/**
 * Centralized mock services for Storybook stories.
 * Import these instead of creating ad-hoc mock classes in each story file.
 */
import { signal, computed } from '@angular/core';
import { of, BehaviorSubject } from 'rxjs';

// ─── UserService ────────────────────────────────────────────────────
export class MockUserService {
  userSignal = signal(null);
  customerFirstnameSignal = computed(() => '');
  isAdminSignal = signal(false);
  currentUser = () => null;
  isAdmin = () => this.isAdminSignal;
  login = () => of(null);
  logout = () => of(null);
}

export class MockLoggedInUserService {
  userSignal = signal({ id: 123, firstname: 'Anders', lastname: 'Jensen', email: 'anders@supervin.dk' });
  customerFirstnameSignal = computed(() => 'Anders');
  isAdminSignal = signal(false);
  currentUser = () => this.userSignal();
  isAdmin = () => this.isAdminSignal;
  login = () => of(null);
  logout = () => of(null);
}

// ─── CartService ────────────────────────────────────────────────────
export class MockCartService {
  private _cartSignal = signal({ cart: { items: [], cartId: null }, loading: false });
  cartSignal = this._cartSignal.asReadonly();
  cartObservable = of(this._cartSignal());
  addToCart = () => of(null);
  updateItemInCart = () => of(null);
  removeItemFromCart = () => of(null);
  getCart = () => of(null);
  currentCart = () => this._cartSignal();
  cart = () => this._cartSignal;
}

export class MockCartWithItemsService {
  private _cartSignal = signal({
    cart: {
      cartId: 'cart-123',
      items: [
        { vismaId: 'VP1001', qty: 2, title: 'Château Margaux 2020', price: 8900 },
        { vismaId: 'VP1002', qty: 1, title: 'Barolo Riserva 2018', price: 6900 },
      ],
    },
    loading: false,
  });
  cartSignal = this._cartSignal.asReadonly();
  cartObservable = of(this._cartSignal());
  addToCart = () => of(null);
  updateItemInCart = () => of(null);
  removeItemFromCart = () => of(null);
  getCart = () => of(null);
  currentCart = () => this._cartSignal();
  cart = () => this._cartSignal;
}

// ─── GroupVoucherService ────────────────────────────────────────────
export class MockGroupVoucherService {
  private _signal = signal({ groupVoucher: null, loading: false });
  groupVoucherSignal = this._signal.asReadonly();
  currentGroupVoucher = () => null;
  applyGroupVoucher = () => Promise.resolve();
  removeGroupVoucher = () => Promise.resolve();
  voucherSearchPageFilter = computed(() => null);
  getGroupVoucher = () => of(null);
}

export class MockActiveGroupVoucherService {
  private _signal = signal({
    groupVoucher: {
      code: 'FIRMAVIN2026',
      delivery: 0,
      freeShipping: true,
      published: true,
      vismaCampaignId: 'VC-100',
      prices: [
        { id: 'gvp-1', price: '6500', productId: '1004', vismaId: 'VP-GV-100' },
      ],
      showSuggestedRetailPrice: false,
      showVatText: false,
    },
    loading: false,
  });
  groupVoucherSignal = this._signal.asReadonly();
  currentGroupVoucher = () => this._signal().groupVoucher;
  applyGroupVoucher = () => Promise.resolve();
  removeGroupVoucher = () => Promise.resolve();
  voucherSearchPageFilter = computed(() => null);
}

// ─── ProductService ─────────────────────────────────────────────────
export class MockProductService {
  getFormattedProductPricesSignal = (productS: { (): any }) => computed(() => {
    const product = productS();
    return product?.priceTable?.pricings ?? [];
  });
  getDefaultPriceSignal = (productS: { (): any }) => computed(() => {
    const product = productS();
    const pricings = product?.priceTable?.pricings ?? [];
    return pricings.find((p: any) => p.minQty === product?.defaultQuantity) ?? pricings[pricings.length - 1];
  });
  getDefaultPriceAsync = (product: any) => {
    const pricings = product?.priceTable?.pricings ?? [];
    const pricing = pricings.find((p: any) => p.minQty === product?.defaultQuantity) ?? pricings[pricings.length - 1];
    return of(pricing);
  };
}

// ─── ProductPriceService ────────────────────────────────────────────
export class MockProductPriceService {
  currencyCode = signal('DKK');
  getUnitName = (product: any) => product?.meta?.units?.shortName ?? 'fl.';
  calculateDiscountPercentage = (product: any) => {
    const pricings = product?.priceTable?.pricings ?? [];
    const regular = pricings.filter((p: any) => p?.amountIncVat > 0).map((p: any) => p.amountIncVat);
    const discounts = pricings.filter((p: any) => p?.discount?.amountIncVat > 0).map((p: any) => p.discount.amountIncVat);
    if (!regular.length || !discounts.length) return 0;
    return Math.floor(((Math.max(...regular) - Math.min(...discounts)) / Math.max(...regular)) * 100);
  };
  priceToPriceLines = (price: any, product: any) => {
    const unitName = product?.meta?.units?.shortName ?? 'fl.';
    const label = `${price.minQty} ${unitName} pr. stk.`;
    const lines: any[] = [];
    const isDiscounted = !!price.discount;
    if (isDiscounted && price.amountIncVat) {
      lines.push({ isDiscounted: false, label, currencyCode: 'DKK', price: price.amountIncVat, isStriked: true, qty: price.minQty, unitName });
    }
    lines.push({
      isDiscounted,
      label,
      currencyCode: 'DKK',
      price: isDiscounted ? (price.discount?.amountIncVat ?? price.amountIncVat) : price.amountIncVat,
      prefix: isDiscounted ? ['BEDSTE', 'PRIS'] : undefined,
      showVatText: true,
      qty: price.minQty,
      unitName,
    });
    return lines;
  };
  formatPriceLines = (_groupVoucher: any, product: any, prices: any[]) => {
    const lines: any[] = [];
    for (const price of prices) {
      const mapped = this.priceToPriceLines(price, product);
      if (mapped) lines.push(...mapped);
    }
    return lines;
  };
}

// ─── StoryblokService ───────────────────────────────────────────────
export class MockStoryblokService {
  getImageUrl = (options: { filename?: string }) => options?.filename ?? '';
  getImageObject = (options: { filename?: string }, width?: number, height?: number) => ({
    imgWidth: width ?? 600,
    imgHeight: height ?? 400,
    optimizedUrl: options?.filename ?? '',
  });
  getCols = () => 'col-12';
}

// ─── StoryblokStylingService ────────────────────────────────────────
export class MockStoryblokStylingService {
  getClassList = () => ['col-12'];
}

// ─── StoryblokComponentService ──────────────────────────────────────
export class MockStoryblokComponentService {
  getComponents = () => ({});
}

// ─── StoryblokDynamicInputService ───────────────────────────────────
export class MockStoryblokDynamicInputService {
  replaceDynamicInputs = (value: string) => signal(value);
}

// ─── TrackEventService ──────────────────────────────────────────────
export class MockTrackEventService {
  trackEvent = (event: string, data: Record<string, unknown>) => {
    console.log('[Storybook Track]', event, data);
  };
  trackClickEvent = (clickEvent: string, data: Record<string, unknown> = {}) => {
    console.log('[Storybook Click]', clickEvent, data);
  };
}

// ─── RoutingService ─────────────────────────────────────────────────
export class MockRoutingService {
  routerSignal = signal('/');
  getUrlFormattedObjectByPath = (path: string) => ({
    isRelative: true,
    search: {},
    fullURL: path,
    hash: undefined,
    pathname: path,
  });
}

// ─── PageService ────────────────────────────────────────────────────
export class MockPageService {
  getPageAsync = () => of({ page: null, head: null, alternateLinks: [] });
}

// ─── PrintService ───────────────────────────────────────────────────
export class MockPrintService {
  setIsPrinting = () => {};
  printDocument = () => {};
}

// ─── StoreConfigService ─────────────────────────────────────────────
export class MockStoreConfigService {
  currencyCode = signal('DKK');
  storeConfig = signal({ currencyCode: 'DKK', freeDeliveryStartsFrom: 299 });
}

// ─── NgbModal ───────────────────────────────────────────────────────
export class MockNgbModal {
  open = () => ({ componentInstance: {}, result: Promise.resolve() });
}

// ─── GoogleService ──────────────────────────────────────────────────
export class MockGoogleService {
  addProductToCartEvent = () => {};
  virtualPageView = () => {};
}

// ─── AlgoliaInsightsService ─────────────────────────────────────────
export class MockAlgoliaInsightsService {
  clickedAddToCart = () => {};
  clickedOnProduct = () => {};
}

// ─── CustomerReviewsAndWishlistService ──────────────────────────────
export class MockCustomerReviewsAndWishlistService {
  customerReviews = signal([
    { __typename: 'CustomerReview', product: { id: '1001' }, rating: 4 },
  ]);
  wishlist = signal([]);
  adjustWishlist = () => {};
  addReviewProduct = () => {};
}

// ─── CloudflareCacheTagService ──────────────────────────────────────
export class MockCloudflareCacheTagService {
  addTag = () => {};
  removeTag = () => {};
}

// ─── ToastService ───────────────────────────────────────────────────
export class MockToastService {
  toasts: any[] = [];
  showDefault = (text: string) => { this.toasts.push({ textOrTpl: text }); };
  showDanger = (text: string) => { this.toasts.push({ textOrTpl: text, classname: 'bg-danger text-light' }); };
  showSuccess = (text: string) => { this.toasts.push({ textOrTpl: text, classname: 'bg-success text-light' }); };
  remove = () => {};
}

// ─── BreadcrumbService ──────────────────────────────────────────────
export class MockBreadcrumbService {
  getCurrentBreadcrumbsAsync = () => of([
    { label: 'Forside', path: '/' },
    { label: 'Rødvin', path: '/rodvin' },
    { label: 'Château Margaux 2020', path: '/rodvin/chateau-margaux-2020' },
  ]);
  getCurrentStyleAsync = () => of('default');
}

// ─── GridService ────────────────────────────────────────────────────
export class MockGridService {
  isMobile = () => false;
  isTablet = () => false;
  isDesktop = () => true;
  isLarge = true;
  isExtraSmallSignal = computed(() => false);
  isSmallSignal = computed(() => true);
  isMediumSignal = computed(() => true);
  isLargeSignal = computed(() => true);
  isExtraLargeSignal = computed(() => true);
}

// ─── NgbActiveModal ─────────────────────────────────────────────────
export class MockNgbActiveModal {
  close = (result?: unknown) => { console.log('[Storybook] Modal closed', result); };
  dismiss = (reason?: unknown) => { console.log('[Storybook] Modal dismissed', reason); };
}

// ─── AgeVerificationService ─────────────────────────────────────────
export class MockAgeVerificationService {
  setAgeVerified = (verified: boolean) => { console.log('[Storybook] Age verified:', verified); };
  isAgeVerified = () => true;
}

// ─── HsFormsService ─────────────────────────────────────────────────
export class MockHsFormsService {
  submitHubspotFormEvent = () => Promise.resolve();
}

// ─── CookiebotLoaderService ─────────────────────────────────────────
export class MockCookiebotLoaderService {
  openConsentDialog = () => { console.log('[Storybook] Cookiebot consent dialog opened'); };
}

// ─── DeliveryService ────────────────────────────────────────────────
export class MockDeliveryService {
  isPostcodeDeliverable = () => of(true);
}

// ─── CookiebotService ───────────────────────────────────────────────
export class MockCookiebotService {
  cookiebotConsent = new BehaviorSubject({ marketing: true, statistics: true, preferences: true });
}

// ─── StoryblokImageService ──────────────────────────────────────────
export class MockStoryblokImageService {
  getImageSize = () => ({ imgWidth: 600, imgHeight: 400 });
}
